import { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { getApiBaseUrl } from '../lib/utils';

interface SubscriptionStatus {
  isSubscribed: boolean;
  subscriptionId?: string;
  planId?: string;
  status?: string;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: number;
  loading: boolean;
  error?: string;
}

export const useSubscription = (): SubscriptionStatus => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    isSubscribed: false,
    loading: true,
    error: undefined,
  });

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        setSubscription({
          isSubscribed: false,
          loading: false,
          error: undefined,
        });
        return;
      }

      try {
        // Reset error state when starting new check
        setSubscription(prev => ({ ...prev, error: undefined }));
        
        // Check subscription status via API
        const response = await fetch(`${getApiBaseUrl()}/api/subscription/status?email=${encodeURIComponent(user.email || '')}`, {
          headers: {
            'Authorization': `Bearer ${await user.getIdToken()}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSubscription({
            isSubscribed: data.isSubscribed,
            subscriptionId: data.subscriptionId,
            planId: data.planId,
            status: data.status,
            cancelAtPeriodEnd: data.cancelAtPeriodEnd,
            currentPeriodEnd: data.currentPeriodEnd,
            loading: false,
            error: undefined,
          });
        } else {
          // Handle HTTP errors more specifically
          const errorMessage = response.status === 404 ? 'Subscription not found' : `HTTP ${response.status}`;
          setSubscription({
            isSubscribed: false,
            loading: false,
            error: errorMessage,
          });
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        const errorMessage = error instanceof Error ? error.message : 'Network error';
        
        setSubscription({
          isSubscribed: false,
          loading: false,
          error: errorMessage,
        });
        
        // Additional logging for debugging
        console.warn('Subscription check failed. User will need to retry or contact support.');
        
        // You might want to show a toast notification here in the future
        // to inform the user that there was an issue checking their subscription
      }
    };

    checkSubscription();
  }, [user]);

  return subscription;
};